import React from 'react';

import type { GradingConfigSnapshotResponse } from '@/common/api/models/gradingConfigSnapshotResponse';
import type { TenantPublicGradingSettingsGradeProjectorAnyOf as GradeProjector } from '@/common/api/models/tenantPublicGradingSettingsGradeProjectorAnyOf';
import type { PerformanceLevelConfig } from '@/common/api/models/performanceLevelConfig';
import {
  useTenantGradingSettings,
  useTenantSettingsStore,
} from '@/common/context/TenantSettingsStore';
import { DEFAULT_ESSAY_PROJECTOR, resolveGradeProjector } from '@/lecturer/EvaluationDesk/utils/gradingConversions';

/**
 * Effective grading config for the open EvaluationDesk submission.
 * When a sealed gradingConfigSnapshot is present, materialize *all* consumer fields
 * from it (keys, labels, performanceLevels, levelWeights, colors, projectors) -
 * keeping live tenant levelWeights while swapping sealed level ids causes "Unknown
 * performance level" warnings and wrong grade aggregation.
 */
export type EvaluationGradingConfigValue = {
  evalDimensionKeys: string[];
  evalDimensions: Record<string, string>;
  performanceLevels: PerformanceLevelConfig[];
  performanceLevelIds: string[];
  oralExamDimensionKeys: string[];
  systemManagedKeys: string[];
  dimensionColors: Record<string, string>;
  dimensionLightColors: Record<string, string>;
  levelWeights: Record<string, number>;
  dimensionMaxWeights: Record<string, number>;
  gradeProjector: GradeProjector;
  taskGradeProjectors: Record<string, GradeProjector>;
  resolveGradeProjector: (taskType: string) => GradeProjector;
};

const EvaluationGradingConfigContext = React.createContext<EvaluationGradingConfigValue | null>(null);

function buildLevelWeights(levels: PerformanceLevelConfig[]): Record<string, number> {
  return Object.fromEntries(levels.map((level) => [level.id, level.performancePercent]));
}

function buildDimensionColors(keys: string[]): Record<string, string> {
  return Object.fromEntries(keys.map((dim, index) => [dim, `$dim${index % 10}700`]));
}

function buildDimensionLightColors(keys: string[]): Record<string, string> {
  return Object.fromEntries(keys.map((dim, index) => [dim, `$dim${index % 10}50`]));
}

function buildDimensionMaxWeights(
  snapshot: GradingConfigSnapshotResponse,
  tenantFallback: Record<string, number | null>,
): Record<string, number | null> {
  const keys = Object.keys(snapshot.evalDimensions ?? {});
  return Object.fromEntries(
    keys.map((key) => [key, snapshot.evalDimensions?.[key]?.maxWeight ?? tenantFallback[key] ?? null]),
  );
}

function buildSystemManagedKeysFromTenant(
  evalDimensionKeys: string[],
  editableDimensionKeys: string[],
): string[] {
  const editable = new Set(editableDimensionKeys);
  return evalDimensionKeys.filter((key) => !editable.has(key));
}

function attachProjectorHelpers(
  config: Omit<EvaluationGradingConfigValue, 'resolveGradeProjector'>,
): EvaluationGradingConfigValue {
  return {
    ...config,
    resolveGradeProjector: (taskType: string) => resolveGradeProjector(
      {
        gradeProjector: config.gradeProjector,
        taskGradeProjectors: config.taskGradeProjectors,
      },
      taskType,
    ),
  };
}

function readTenantProjectors(settings: ReturnType<typeof useTenantSettingsStore.getState>['settings']) {
  const grading = settings?.gradingSettings;
  return {
    gradeProjector: (grading?.gradeProjector ?? DEFAULT_ESSAY_PROJECTOR) as GradeProjector,
    taskGradeProjectors: (grading?.taskGradeProjectors ?? {}) as Record<string, GradeProjector>,
  };
}

function buildTenantGradingConfig(
  tenant: ReturnType<typeof useTenantGradingSettings>,
  tenantProjectors: { gradeProjector: GradeProjector; taskGradeProjectors: Record<string, GradeProjector> },
): EvaluationGradingConfigValue {
  return attachProjectorHelpers({
    evalDimensionKeys: tenant.evalDimensionKeys,
    evalDimensions: tenant.evalDimensions,
    performanceLevels: tenant.performanceLevels,
    performanceLevelIds: tenant.performanceLevelIds,
    oralExamDimensionKeys: tenant.oralExamDimensionKeys,
    systemManagedKeys: buildSystemManagedKeysFromTenant(
      tenant.evalDimensionKeys,
      tenant.editableDimensionKeys,
    ),
    dimensionColors: tenant.dimensionColors,
    dimensionLightColors: tenant.dimensionLightColors,
    levelWeights: tenant.levelWeights,
    dimensionMaxWeights: tenant.dimensionMaxWeights,
    gradeProjector: tenantProjectors.gradeProjector,
    taskGradeProjectors: tenantProjectors.taskGradeProjectors,
  });
}

function buildConfigFromSnapshot(
  snapshot: GradingConfigSnapshotResponse,
  tenantFallback: EvaluationGradingConfigValue,
): EvaluationGradingConfigValue {
  const evalDimensionKeys = Object.keys(snapshot.evalDimensions ?? {});
  const evalDimensions = Object.fromEntries(
    evalDimensionKeys.map((key) => [key, snapshot.evalDimensions?.[key]?.description ?? key]),
  );
  const performanceLevels = snapshot.performanceLevels ?? tenantFallback.performanceLevels;
  const colorKeys = Array.from(new Set([
    ...evalDimensionKeys,
    ...tenantFallback.oralExamDimensionKeys,
  ]));

  return attachProjectorHelpers({
    ...tenantFallback,
    evalDimensionKeys,
    evalDimensions,
    performanceLevels,
    performanceLevelIds: performanceLevels.map((level) => level.id),
    systemManagedKeys: evalDimensionKeys.filter(
      (key) => snapshot.evalDimensions?.[key]?.systemManaged === true,
    ),
    levelWeights: buildLevelWeights(performanceLevels),
    dimensionMaxWeights: buildDimensionMaxWeights(snapshot, tenantFallback.dimensionMaxWeights),
    dimensionColors: buildDimensionColors(colorKeys),
    dimensionLightColors: buildDimensionLightColors(colorKeys),
    gradeProjector: snapshot.gradeProjector ?? tenantFallback.gradeProjector,
    taskGradeProjectors: snapshot.taskGradeProjectors ?? tenantFallback.taskGradeProjectors,
  });
}

export function EvaluationGradingConfigProvider({
  snapshot,
  children,
}: {
  snapshot?: GradingConfigSnapshotResponse | null;
  children: React.ReactNode;
}) {
  const tenant = useTenantGradingSettings();
  const settings = useTenantSettingsStore((state) => state.settings);
  const tenantProjectors = React.useMemo(() => readTenantProjectors(settings), [settings]);
  const tenantConfig = React.useMemo(
    () => buildTenantGradingConfig(tenant, tenantProjectors),
    [tenant, tenantProjectors],
  );
  const value = React.useMemo(
    () => (snapshot ? buildConfigFromSnapshot(snapshot, tenantConfig) : null),
    [snapshot, tenantConfig],
  );

  return (
    <EvaluationGradingConfigContext.Provider value={value}>
      {children}
    </EvaluationGradingConfigContext.Provider>
  );
}

/** Prefer sealed snapshot grading config when a provider supplies it; else tenant settings. */
export function useEvaluationGradingConfig(): EvaluationGradingConfigValue {
  const snapshotConfig = React.useContext(EvaluationGradingConfigContext);
  const tenant = useTenantGradingSettings();
  const settings = useTenantSettingsStore((state) => state.settings);
  const tenantProjectors = React.useMemo(() => readTenantProjectors(settings), [settings]);
  const tenantConfig = React.useMemo(
    () => buildTenantGradingConfig(tenant, tenantProjectors),
    [tenant, tenantProjectors],
  );
  return snapshotConfig ?? tenantConfig;
}
